<template>
  <div
    class="modal"
    :class="{ 'is-active': value }">

    <div class="modal-background"
      @click="close()">
    </div>
    
    <div class="modal-content">
      <div class="modal-card">

        <header class="modal-card-head">
          <p class="modal-card-title">Manage Elevations</p>
          <button
            class="delete"
            aria-label="close"
            @click="close(true)">
          </button>
        </header>

        <div class="modal-card-body">

          <article 
            v-if="!loading && elevations.length == 0" 
            class="message is-info is-fullwidth mt-3">
            <div class="message-body">
              There are no elevations.
            </div>
          </article>

          <v-fade-transition mode="out-in">
            <div 
              v-if="loading" 
              class="is-relative">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <table 
              v-else
              class="table is-fullwidth is-hoverable">

              <thead>
                <tr>
                  <th 
                    class="text--nowrap"
                    v-for="(header, headerIndex) in headers" 
                    :key="headerIndex">
                    {{ header.label }}
                  </th>
                  <th></th>
                </tr>
              </thead>

              <tfoot>
                <tr>
                  <td :colspan="headers.length + 1">
                    <div class="centered">
                      <button
                        v-if="!adding"
                        @click="adding = true" 
                        class="button is-primary is-small">
                        Add a new elevation
                      </button>
                    </div>
                  </td>
                </tr>
              </tfoot>

              <tbody>
                <tr 
                  class="v-centered"
                  v-for="elevation in elevations" 
                  :key="elevation.id">

                  <template v-if="editingId != elevation.id">
                    <td>
                      {{ elevation.elevation_name }}
                    </td>
                    <td>
                      {{ elevation.default_sun_hours }}
                    </td>
                  </template>

                  <template v-else>
                    <td>
                      <v-text-field 
                        required
                        placeholder="Enter Elevation Name"
                        v-model="elevation.elevation_name">
                      </v-text-field>
                    </td>
                    <td>
                      <v-text-field 
                        placeholder="Enter Default Sun Hours"
                        type="number"
                        v-model="elevation.default_sun_hours">
                      </v-text-field>
                    </td>
                  </template>
                  <td>
                    <div class="buttons buttons-right">
                      <template v-if="editingId != elevation.id">
                        <button
                          @click="editingId = elevation.id" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-edit has-text-grey"></i>
                          </span>
                        </button>
                        <button
                          @click="launchConfirmDelete(elevation.id)" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-trash has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button
                          @click="update(elevation)" 
                          class="button is-primary is-small">
                          <span class="icon">
                            <i class="fas fa-save"></i>
                          </span>
                        </button>
                        <button
                          @click="editingId = null" 
                          class="button is-small">
                          <span class="icon">
                            <i class="fas fa-times has-text-grey"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
                <tr class="v-centered" v-if="adding">
                  <td>
                    <v-text-field 
                      required
                      placeholder="Enter Elevation Name"
                      v-model="elevation.elevation_name">
                    </v-text-field>
                  </td>
                  <td>
                    <v-text-field 
                      placeholder="Enter Default Sun Hours"
                      type="number"
                      v-model="elevation.default_sun_hours">
                    </v-text-field>
                  </td>
                  <td>
                    <div class="buttons buttons-right">
                      <button
                        @click="add" 
                        class="button is-primary is-small">
                        <span class="icon">
                          <i class="fas fa-save"></i>
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>  
          </v-fade-transition>

        </div>

        <v-confirm-modal 
          v-model="confirmDelete.state"
          :title="confirmDelete.title"
          :message="confirmDelete.message"
          :type="confirmDelete.type"
          @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
          @cancel="confirmDelete.state = false"/>

      </div>
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VConfirmModal from '@/components/base/VConfirmModal'
import VSpinLoader from '@/components/base/VSpinLoader'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean
    }
  },
  components: { VTextField, VConfirmModal, VSpinLoader, VFadeTransition },
  data() {
    return {
      headers: [
        { label: 'Name' },
        { label: 'Default Sun Hours' }
      ],
      elevation: {
        elevation_name: '',
        default_sun_hours: null
      },
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this elevation?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      valid: false,
      adding: false,
      editingId: null
    }
  },
  computed: {
    ...mapState("elevations", [
      "elevations", "loading"
    ])
  },
  watch: {
    value(v) {
      if (!v) {
        this.adding = false;
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("elevations", [
      "getElevations","addElevation", "deleteElevation", "updateElevation",
      "toggleAllSelected"
    ]),
    ...mapMutations("elevations", [
      "selectAll", "deselectAll", "toggleSelected"
    ]),
    initialize() {
      if (this.elevations.length == 0) {
        this.getElevations();
      }
    },
    add() {
      this.addElevation(this.elevation).then(() => {
        this.adding = false;
        this.clearData();
      });
    },
    update(elevation) {
      let data = {};

      Object.keys(this.elevation).forEach(field => {
        data[field] = elevation[field]
      });

      data.default_sun_hours = parseFloat(data.default_sun_hours);

      this.updateElevation(
        { elevationId: elevation.id, data: data }
      ).then(() => {
        this.editingId = null;
      })
    },
    launchConfirmDelete(id) {
      this.deleteConfirmedCb = () => {
        this.deleteElevation(id);
        this.confirmDelete.state = false;
      }
      this.confirmDelete.message = 'Are you sure you want to delete this elevation?';
      this.confirmDelete.state = true;
    },
    close(clear=false) {
      this.$emit('input', false);
      if (clear) {
        this.clearData();
      }
    },
    clearData() {
      Object.assign(this.$data, this.$options.data.call(this));
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';

.table tfoot td .centered {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.modal-card {
  border-radius: 6px;
}
</style>
